import { FileImage, Upload, X } from 'lucide-react';
import { useId, useRef, useState, type DragEvent } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { formatFileSize } from '@/lib/format';
import { cn } from '@/lib/utils';

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

/** Anchored, so a type merely *containing* "image/" no longer passes. */
const IMAGE_MIME = /^image\/(png|jpeg|jpg|gif|webp|svg\+xml)$/i;

export interface FileUploadProps {
  readonly label: string;
  readonly selectedFile: File | null;
  readonly onSelect: (file: File) => void;
  readonly onRemove: () => void;
  readonly supportText?: string;
  readonly maxBytes?: number;
}

export function FileUpload({
  label,
  selectedFile,
  onSelect,
  onRemove,
  supportText = 'PNG, JPG, WEBP oder SVG — SVG liefert die beste Qualität.',
  maxBytes = DEFAULT_MAX_BYTES,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const accept = (file: File): void => {
    if (!IMAGE_MIME.test(file.type)) {
      setError('Nicht unterstütztes Format. Erlaubt sind PNG, JPG, GIF, WEBP und SVG.');
      return;
    }
    if (file.size > maxBytes) {
      setError(`Die Datei ist zu groß. Maximal ${formatFileSize(maxBytes)}.`);
      return;
    }
    setError(null);
    onSelect(file);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragOver(false);
    const [file] = Array.from(event.dataTransfer.files);
    if (file !== undefined) accept(file);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        className="sr-only"
        onChange={(event) => {
          const [file] = Array.from(event.target.files ?? []);
          if (file !== undefined) accept(file);
          // Allow re-selecting the same file after a removal.
          event.target.value = '';
        }}
      />

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={onDrop}
        className={cn(
          'rounded-lg border-2 border-dashed p-4 text-center transition-colors',
          isDragOver ? 'border-primary bg-primary/10' : 'border-muted-foreground/25',
        )}
      >
        <FileImage className="mx-auto mb-2 h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          <Upload aria-hidden="true" />
          Datei auswählen
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">oder hierher ziehen</p>
        <p className="text-xs text-muted-foreground">{supportText}</p>
      </div>

      {selectedFile !== null && (
        <div className="flex items-center justify-between gap-2 rounded-md bg-muted p-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileImage className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <div className="min-w-0">
              <span className="block truncate text-sm">{selectedFile.name}</span>
              <span className="text-xs text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </span>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10"
            onClick={() => {
              setError(null);
              onRemove();
            }}
          >
            <X aria-hidden="true" />
            <span className="sr-only">{label} entfernen</span>
          </Button>
        </div>
      )}

      {error !== null && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
